//
// Copyright 2025 DXOS.org
//

import { QueryAST } from '@dxos/echo-protocol';
import { EID, SpaceId } from '@dxos/keys';

/**
 * Lists spaces this query will select from.
 */
export const getTargetSpacesForQuery = (query: QueryAST.Query): SpaceId[] => {
  const spaces = new Set<SpaceId>();

  const visitor = (node: QueryAST.Query) => {
    if (node.type === 'from' && node.from._tag === 'scope') {
      for (const scope of node.from.scopes) {
        // A space scope without `spaceId` targets the owning space; it adds no
        // explicit space restriction (resolved by the executing database).
        if (scope._tag === 'space' && scope.spaceId !== undefined) {
          spaces.add(SpaceId.make(scope.spaceId));
        }
      }
    }
  };
  QueryAST.visit(query, visitor);
  return [...spaces];
};

const filterContainsTimestamp = (filter: QueryAST.Filter) => {
  if (filter.type === 'timestamp') {
    return true;
  }
  if (filter.type === 'and' || filter.type === 'or') {
    return filter.filters.some(filterContainsTimestamp);
  }
  if (filter.type === 'not') {
    return filterContainsTimestamp(filter.filter);
  }
  return false;
};

const filterContainsChildOf = (filter: QueryAST.Filter): boolean => {
  if (filter.type === 'child-of') {
    return true;
  }
  if (filter.type === 'and' || filter.type === 'or') {
    return filter.filters.some(filterContainsChildOf);
  }
  if (filter.type === 'not') {
    return filterContainsChildOf(filter.filter);
  }
  return false;
};

/**
 * Whether the filter is a subquery-membership predicate (`Filter.in(query.project(...))`, AST
 * type `in-query`) or composes one via `and` / `or` / `not` / a nested `object` prop. Unlike
 * `child-of`, which is only ever a root filter, `in-query` is always nested inside an `object`
 * filter's `props` — so unlike `filterContainsChildOf` above, this must also recurse into `props`.
 */
const filterContainsInQuery = (filter: QueryAST.Filter): boolean => {
  if (filter.type === 'in-query') {
    return true;
  }
  if (filter.type === 'object') {
    return Object.values(filter.props).some(filterContainsInQuery);
  }
  if (filter.type === 'and' || filter.type === 'or') {
    return filter.filters.some(filterContainsInQuery);
  }
  if (filter.type === 'not') {
    return filterContainsInQuery(filter.filter);
  }
  return false;
};

/**
 * Whether the query carries an `order`/`skip`/`limit` clause anywhere in its AST.
 *
 * These clauses impose windowing semantics (a specific ordering, or a slice of a larger result
 * set) that only make sense when computed over the *complete* candidate set. `GraphQueryContext`
 * merges results from multiple independent sources (working set, SQL index, registry, ...) without
 * a global re-sort/re-slice, so a source that applied its own local limit/order would silently
 * return the wrong window while a sibling source contributes overlapping or additional items.
 * Callers use this to defer such queries entirely to the SQL-backed/feed sources that compute the
 * full window themselves.
 */
export const queryHasWindowing = (query: QueryAST.Query): boolean => {
  let hasWindowing = false;
  QueryAST.visit(query, (node) => {
    if (node.type === 'order' || node.type === 'skip' || node.type === 'limit') {
      hasWindowing = true;
    }
  });
  return hasWindowing;
};

/**
 * Whether an aggregate clause needs the index rather than the tab's working set: a count over
 * whatever the tab happens to hold is not a count of the space, and a `timestamp` key needs index
 * timestamps a core does not carry.
 */
export const aggregateNeedsIndex = (aggregates: readonly QueryAST.GroupAggregate[]): boolean =>
  !aggregates.some((aggregate) => aggregate.kind === 'items') ||
  aggregates.some((aggregate) => aggregate.kind === 'timestamp');

/** {@link aggregateNeedsIndex} over a query's `aggregate` clause, if it has one. */
export const queryAggregateNeedsIndex = (query: QueryAST.Query): boolean => {
  let needsIndex = false;
  QueryAST.visit(query, (node) => {
    if (node.type === 'aggregate' && aggregateNeedsIndex(node.aggregates)) {
      needsIndex = true;
    }
  });
  return needsIndex;
};

/**
 * The query's disposition toward deleted objects, defaulting to `'exclude'` when it carries no
 * `options` clause.
 *
 * Read by an AST walk rather than through {@link isSimpleSelectionQuery} so it also answers for the
 * windowed and complex queries that helper deliberately rejects.
 */
export const getQueryDeletedOption = (query: QueryAST.Query): 'exclude' | 'include' | 'only' => {
  let deleted: QueryAST.QueryOptions['deleted'];
  QueryAST.visit(query, (node) => {
    if (node.type === 'options' && node.options.deleted !== undefined) {
      deleted = node.options.deleted;
    }
  });
  return deleted ?? 'exclude';
};

/**
 * Extracts the filter and options from a query.
 * Supports Select(...), Options(Select(...)), and From(Select(...)) queries.
 *
 * Deliberately returns `null` for queries carrying `order`/`skip`/`limit` clauses -- callers
 * (e.g. `GraphQueryContext`) use that as the signal that this query has semantics (windowing,
 * ordering) they cannot contribute to, and defer entirely to the SQL-backed/feed sources that
 * can.
 */
export const isSimpleSelectionQuery = (
  query: QueryAST.Query,
): { filter: QueryAST.Filter; options?: QueryAST.QueryOptions; hasQueues?: boolean } | null => {
  switch (query.type) {
    case 'options': {
      const maybeFilter = isSimpleSelectionQuery(query.query);
      if (!maybeFilter) {
        return null;
      }
      return {
        filter: maybeFilter.filter,
        options: query.options,
        hasQueues: maybeFilter.hasQueues,
      };
    }
    case 'from': {
      const maybeFilter = isSimpleSelectionQuery(query.query);
      if (!maybeFilter) {
        return null;
      }
      const hasQueues =
        (query.from._tag === 'scope' && query.from.scopes.some((s) => s._tag === 'feed')) || maybeFilter.hasQueues;
      return {
        filter: maybeFilter.filter,
        options: maybeFilter.options,
        hasQueues: hasQueues || false,
      };
    }
    case 'select': {
      if (
        filterContainsTimestamp(query.filter) ||
        filterContainsChildOf(query.filter) ||
        filterContainsInQuery(query.filter)
      ) {
        return null;
      }
      return { filter: query.filter, options: undefined };
    }
    default: {
      return null;
    }
  }
};

/**
 * Ids or typenames of which a simple selection's filter requires a match to have one; undefined when it requires
 * neither, so any written object might match.
 */
export const getSelectionKeys = (filter: QueryAST.Filter): { ids: string[]; typenames: string[] } | undefined => {
  switch (filter.type) {
    case 'object':
      if (filter.id && filter.id.length > 0) {
        return { ids: [...filter.id], typenames: [] };
      }
      return filter.typename !== null ? { ids: [], typenames: [filter.typename] } : undefined;
    case 'and':
      // Any one conjunct's requirement holds for the whole conjunction.
      for (const conjunct of filter.filters) {
        const keys = getSelectionKeys(conjunct);
        if (keys) {
          return keys;
        }
      }
      return undefined;
    case 'or': {
      const ids: string[] = [];
      const typenames: string[] = [];
      for (const branch of filter.filters) {
        const keys = getSelectionKeys(branch);
        if (!keys) {
          return undefined;
        }
        ids.push(...keys.ids);
        typenames.push(...keys.typenames);
      }
      return filter.filters.length > 0 ? { ids, typenames } : undefined;
    }
    default:
      return undefined;
  }
};

/**
 * The parents a query selects under, for a query whose membership is otherwise decided by each object's own
 * document: a select with `child-of` conjuncts, or the children of a by-id selection. Undefined for any other shape.
 */
export type HierarchyScope = {
  /** Ids of the parents members sit under. */
  anchors: ReadonlySet<string>;
  /** How many parent hops a member may sit below an anchor. */
  depth: number;
};

/** Matches the executors' `child-of` walk. */
const MAX_CHILD_OF_DEPTH = 10;

export const getHierarchyScope = (query: QueryAST.Query): HierarchyScope | undefined => {
  switch (query.type) {
    case 'options':
      return getHierarchyScope(query.query);
    case 'from':
      return query.from._tag === 'scope' ? getHierarchyScope(query.query) : undefined;
    case 'filter':
      return isOwnDocumentFilter(query.filter) ? getHierarchyScope(query.selection) : undefined;
    case 'select':
      return childOfScope(query.filter);
    case 'hierarchy-traversal': {
      const anchor = query.anchor;
      if (query.direction !== 'to-children' || anchor.type !== 'select' || anchor.filter.type !== 'object') {
        return undefined;
      }
      const ids = anchor.filter.id ?? [];
      return ids.length > 0 ? { anchors: new Set(ids), depth: 1 } : undefined;
    }
    default:
      return undefined;
  }
};

const childOfScope = (filter: QueryAST.Filter): HierarchyScope | undefined => {
  const conjuncts = filter.type === 'and' ? filter.filters : [filter];
  const anchors = new Set<string>();
  let depth = 0;
  for (const conjunct of conjuncts) {
    if (conjunct.type === 'child-of') {
      for (const parent of conjunct.parents) {
        const id = EID.getEntityId(parent);
        if (id) {
          anchors.add(id);
        }
      }
      depth = Math.max(depth, conjunct.transitive ? MAX_CHILD_OF_DEPTH : 1);
    } else if (!isOwnDocumentFilter(conjunct)) {
      return undefined;
    }
  }
  return depth > 0 ? { anchors, depth } : undefined;
};

/** Whether each object's own document decides the filter. */
const isOwnDocumentFilter = (filter: QueryAST.Filter): boolean =>
  !filterContainsTimestamp(filter) && !filterContainsChildOf(filter) && !filterContainsInQuery(filter);

/**
 * Whether the query selects from spaces or feeds — the data that space-backed sources
 * (`SpaceQuerySource`, `IndexQuerySource`) can serve.
 *
 * A query with no explicit `from` scope defaults to the owning space and returns true.
 * A query whose explicit scopes contain no space/feed entry (e.g. registry-only) returns
 * false: it is answered entirely by other sources (`RegistryQuerySource`), and forwarding
 * it to a space-backed source is at best wasted work — the edge query host rejects such
 * queries outright ("Query must specify at least one spaceId in options"), failing the
 * whole query even though the registry source produced results.
 */
export const queryTargetsSpacesOrFeeds = (query: QueryAST.Query): boolean => {
  let hasExplicitNonEmptyScope = false;
  let hasSpaceOrFeedScope = false;
  QueryAST.visit(query, (node) => {
    if (node.type === 'from' && node.from._tag === 'scope' && node.from.scopes.length > 0) {
      hasExplicitNonEmptyScope = true;
      if (node.from.scopes.some((scope) => scope._tag === 'space' || scope._tag === 'feed')) {
        hasSpaceOrFeedScope = true;
      }
    }
  });
  return !hasExplicitNonEmptyScope || hasSpaceOrFeedScope;
};

export type RegistryQueryScope = { included: boolean; locations: ReadonlySet<'local' | 'remote'> };

/**
 * Determines whether a query targets the in-process or remote registry.
 *
 * The registry is opt-in: a query only includes it when its `from` clause carries an
 * explicit registry scope (`Scope.registry(...)`).
 *
 * - No `from` clause → excluded (owning-space query; registry not consulted).
 * - `from` clause with no registry scope entries → excluded.
 * - `from` clause with registry scope entries → include the listed locations.
 */
export const getRegistryScopeForQuery = (query: QueryAST.Query): RegistryQueryScope => {
  const clause = findFromClause(query);
  if (clause === null) {
    return { included: false, locations: new Set() };
  }

  if (clause.from._tag !== 'scope') {
    return { included: false, locations: new Set() };
  }

  const registryScopes = clause.from.scopes.filter((s): s is QueryAST.RegistryScope => s._tag === 'registry');
  if (registryScopes.length === 0) {
    return { included: false, locations: new Set() };
  }

  return {
    included: true,
    locations: new Set(registryScopes.map((s) => s.location)),
  };
};

/**
 * Finds the (last) `from` clause within a query AST.
 * Reading the closure-assigned result through a function return boundary preserves
 * the nullable type — a bare local would be narrowed to `null` by control-flow analysis,
 * which cannot observe the assignment inside the `visit` callback.
 */
const findFromClause = (query: QueryAST.Query): QueryAST.QueryFromClause | null => {
  let found: QueryAST.QueryFromClause | null = null;
  QueryAST.visit(query, (node) => {
    if (node.type === 'from') {
      found = node;
    }
  });
  return found;
};

/** Whether a space scope of the query also selects every feed of its space (`includeFeeds`). */
export const queryIncludesAllFeeds = (query: QueryAST.Query): boolean => {
  let includes = false;
  QueryAST.visit(query, (node) => {
    if (
      node.type === 'from' &&
      node.from._tag === 'scope' &&
      node.from.scopes.some((scope) => scope._tag === 'space' && scope.includeAllFeeds === true)
    ) {
      includes = true;
    }
  });
  return includes;
};

/** The branches two scopes of one feed read together; `undefined`, every branch, absorbs any list. */
const unionBranches = (
  left: ReadonlyArray<string | null> | undefined,
  right: ReadonlyArray<string | null> | undefined,
): ReadonlyArray<string | null> | undefined => {
  if (left === undefined || right === undefined) {
    return undefined;
  }
  return [...new Set([...left, ...right])];
};

/**
 * The branches each feed the query's scopes name explicitly reads, `null` naming main; `undefined` reads
 * every branch. An absent scope filter reads main only, and several scopes of one feed read their union.
 */
export const getTargetFeedBranchesForQuery = (
  query: QueryAST.Query,
): ReadonlyMap<string, ReadonlyArray<string | null> | undefined> => {
  const feeds = new Map<string, ReadonlyArray<string | null> | undefined>();
  QueryAST.visit(query, (node) => {
    if (node.type === 'from' && node.from._tag === 'scope') {
      for (const scope of node.from.scopes) {
        if (scope._tag === 'feed') {
          const branches = scope.branches === 'all' ? undefined : (scope.branches ?? [null]);
          feeds.set(
            scope.feedUri,
            feeds.has(scope.feedUri) ? unionBranches(feeds.get(scope.feedUri), branches) : branches,
          );
        }
      }
    }
  });
  return feeds;
};
